
import React, { useState, useEffect, useRef } from 'react';
import { ArtItem, Order, OrderStatus, ChatThread, Comment, ChatMessage, ArtCategory, Language, CartItem, DailyVisit, CommissionTier, SiteSettings } from '../types';
import { Plus, Trash2, X, Flower, CheckCircle2, XCircle, Clock, Hash, User, Calendar, DollarSign, ExternalLink, Box, Loader2, Save, Image as ImageIcon, Upload, Ban, ChevronRight, Download, RefreshCw, AlertCircle, ToggleLeft, ToggleRight, Link as LinkIcon, MessageSquare, Move, Type, Palette, LayoutGrid, RotateCcw, FileVideo, Globe, Edit2, ChevronDown, UserCheck, CloudUpload, BarChart3, TrendingUp, Users, Pipette, ShieldCheck, Star, Sparkles, Zap, Settings, HelpCircle } from 'lucide-react';
import { database } from '../services/databaseService';
import { storage } from '../services/storageService';
import { MASTER_FALLBACK_IMAGE, DEFAULT_CATEGORY_COVERS, ART_SUBCATEGORIES, ART_TYPES } from '../constants';

interface AdminPanelProps {
  arts: ArtItem[];
  orders: Order[];
  chatThreads: ChatThread[];
  comments: Comment[];
  categoryCovers: Record<string, string>;
  commissionTiers: CommissionTier[];
  siteSettings: SiteSettings;
  onUpdateArts: (updater: ArtItem[] | ((prev: ArtItem[]) => ArtItem[])) => void;
  onUpdateOrders: (updater: Order[] | ((prev: Order[]) => Order[])) => void;
  onUpdateSupport: (updater: any) => void;
  onUpdateChatThreads: (updater: ChatThread[] | ((prev: ChatThread[]) => ChatThread[])) => void;
  onUpdateComments: (updater: Comment[] | ((prev: Comment[]) => Comment[])) => void;
  onUpdateCategoryCovers: (covers: Record<string, string>) => void;
  onUpdateCommissionTiers: (tiers: CommissionTier[]) => void;
  onUpdateSiteSettings: (settings: SiteSettings) => void;
  onClose: () => void;
  language: Language;
  notifications?: { orders: number, chats: number };
  onClearNotifications?: (type: 'orders' | 'chats') => void;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ 
  arts, orders, chatThreads, comments, categoryCovers, commissionTiers: initialCommissionTiers, siteSettings,
  onUpdateArts, onUpdateOrders, onUpdateChatThreads, onUpdateComments, onUpdateCategoryCovers, onUpdateCommissionTiers, onUpdateSiteSettings, onClose,
  language, notifications, onClearNotifications
}) => {
  const [activeTab, setActiveTab] = useState<'gallery' | 'orders' | 'chats' | 'categories' | 'commissions' | 'reviews' | 'stats' | 'settings' | 'footer' | 'faq'>('gallery');
  const [selectedThreadEmail, setSelectedThreadEmail] = useState<string | null>(null);
  const [adminReply, setAdminReply] = useState('');
  const [commentReplyTexts, setCommentReplyTexts] = useState<Record<string, string>>({});
  const [isSyncing, setIsSyncing] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  
  const [commissionTiers, setCommissionTiers] = useState<CommissionTier[]>(initialCommissionTiers);
  const [editingTier, setEditingTier] = useState<string | null>(null);
  const [tierPreview, setTierPreview] = useState<string | null>(null);
  const [tierPendingFile, setTierPendingFile] = useState<File | null>(null);
  const tierFileInputRef = useRef<HTMLInputElement>(null);

  const [analytics, setAnalytics] = useState<DailyVisit[]>([]);
  const [isAddingArt, setIsAddingArt] = useState(false);
  const [siteSettingsState, setSiteSettingsState] = useState(siteSettings);
  const [editingArt, setEditingArt] = useState<ArtItem | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [catCoverPreview, setCatCoverPreview] = useState<string | null>(null);
  const [catPendingFile, setCatPendingFile] = useState<File | null>(null);
  const catFileInputRef = useRef<HTMLInputElement>(null);

  const [newArt, setNewArt] = useState<Partial<ArtItem>>({
    title: { pt: '', en: '' },
    category: ArtCategory.ANIMATED,
    artType: 'Single Artwork',
    price: 3.00,
    description: { pt: '', en: '' },
    isAnimated: true,
    steamBackgroundUrl: '',
    notForSale: false
  });

  const chatEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editFormRef = useRef<HTMLDivElement>(null);
  
  const safeOrders = Array.isArray(orders) ? orders : [];
  const safeArts = Array.isArray(arts) ? arts : [];

  const cleanComments = React.useMemo(() => {
    return Array.isArray(comments)
      ? comments
          .filter(c => c && c.id && !c.id.startsWith('SEED-'))
          .filter((comment, index, self) => self.findIndex(c => c.id === comment.id) === index)
      : [];
  }, [comments]);

  useEffect(() => {
    if (activeTab === 'orders') {
      refreshOrders();
      onClearNotifications?.('orders');
    }
    if (activeTab === 'chats') {
      refreshChats();
      onClearNotifications?.('chats');
    }
    if (activeTab === 'stats') refreshAnalytics();
    if (activeTab === 'commissions') refreshCommissionTiers();
  }, [activeTab]);

  const refreshCommissionTiers = async () => {
    setIsSyncing(true);
    const data = await database.getCommissionTiers();
    setCommissionTiers(data);
    setIsSyncing(false);
  };

  const handleDeleteComment = async (commentId: string) => {
    if (confirm(language === 'pt' ? 'Tem certeza que deseja deletar esta avaliação?' : 'Are you sure you want to delete this review?')) {
      const updated = comments.filter(c => c.id !== commentId);
      onUpdateComments(updated);
      await database.saveAllComments(updated);
    }
  };

  const handleSaveReply = async (commentId: string, text: string) => {
    if (!text.trim()) return;
    const updated = comments.map(c => {
      if (c.id === commentId) {
        return { ...c, reply: text };
      }
      return c;
    });
    onUpdateComments(updated);
    await database.saveAllComments(updated);
    setCommentReplyTexts(prev => ({ ...prev, [commentId]: '' }));
  };

  const handleDeleteReply = async (commentId: string) => {
    if (confirm(language === 'pt' ? 'Tem certeza que deseja remover esta resposta?' : 'Are you sure you want to delete this reply?')) {
      const updated = comments.map(c => {
        if (c.id === commentId) {
          const { reply, ...rest } = c;
          return rest;
        }
        return c;
      });
      onUpdateComments(updated);
      await database.saveAllComments(updated);
    }
  };

  useEffect(() => {
    setSiteSettingsState(siteSettings);
  }, [siteSettings]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedThreadEmail, chatThreads, activeTab]);

  const refreshAnalytics = async () => {
    setIsSyncing(true);
    const data = await database.getAnalytics();
    setAnalytics(data);
    setIsSyncing(false);
  };

  const isVideoUrl = (url?: string) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.includes('.mp4') || lower.includes('blob:') || lower.startsWith('data:video/');
  };

  const refreshOrders = async () => {
    setIsSyncing(true);
    const res = await database.getOrders();
    if (res.data) onUpdateOrders(res.data);
    setIsSyncing(false);
  };

  const refreshChats = async () => {
    setIsSyncing(true);
    const chats = await database.getChatThreads();
    if (chats) onUpdateChatThreads(chats);
    setIsSyncing(false);
  };

  const handleGlobalSyncCovers = async () => {
    if (!confirm("Confirmar sincronização com servidor Hostinger?")) return;
    setIsSyncing(true);
    const success = await database.syncAllCoversToServer(categoryCovers);
    if (success) alert("Santuário Global Sincronizado!");
    setIsSyncing(false);
  };

  const handleDownloadCoversJSON = () => {
    const cleanCovers: Record<string, string> = {};
    Object.entries(categoryCovers).forEach(([cat, url]: [string, string]) => {
      if (url.includes('/uploads/')) {
        cleanCovers[cat] = url.split('/').pop() || '';
      } else {
        cleanCovers[cat] = url;
      }
    });
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(cleanCovers, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", "category_covers.json");
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const handleSendAdminReply = async () => {
    if (!adminReply.trim() || !selectedThreadEmail) return;
    const thread = chatThreads.find(t => t.userEmail === selectedThreadEmail);
    const newMessage: ChatMessage = {
      id: `ADMIN-${Date.now()}`,
      sender: 'admin',
      text: adminReply,
      timestamp: new Date().toLocaleTimeString()
    };
    const success = await database.saveChatMessage(selectedThreadEmail, thread?.userName || 'Usuário', newMessage);
    if (!success) {
      alert("Erro ao salvar mensagem no servidor Hostinger. Verifique permissões do arquivo chats.json.");
    }
    onUpdateChatThreads(prev => {
      const up = [...prev];
      const idx = up.findIndex(t => t.userEmail === selectedThreadEmail);
      if (idx > -1) {
        up[idx] = { ...up[idx], messages: [...up[idx].messages, newMessage], lastUpdated: new Date().toISOString() };
      }
      return up;
    });
    setAdminReply('');
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPendingFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      if (file.type.startsWith('video/')) setNewArt(prev => ({ ...prev, isAnimated: true }));
    }
  };

  const handleEditClick = (art: ArtItem) => {
    setEditingArt(art);
    setNewArt({ 
      ...art, 
      subcategory: art.subcategory || (ART_SUBCATEGORIES[art.category]?.[0]?.id || ''),
      artType: art.artType || 'Single Artwork'
    });
    setPreviewUrl(art.videoUrl || art.imageUrl);
    setIsAddingArt(true);
    setTimeout(() => {
      if (editFormRef.current) {
        editFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleDeleteArt = async (id: string) => {
    if (!confirm("Excluir esta obra?")) return;
    const success = await database.deleteArt(id);
    if (success) onUpdateArts(prev => prev.filter(a => a.id !== id));
  };

  const handleDeleteOrder = async (id: string) => {
    if (!confirm("Excluir este pedido permanentemente?")) return;
    const success = await database.deleteOrder(id);
    if (success) {
      onUpdateOrders(prev => prev.filter(o => o.id !== id));
      if (expandedOrderId === id) setExpandedOrderId(null);
    }
  };

  const handleDeleteChatThread = async (email: string) => {
    if (!confirm("Excluir conversa com este usuário?")) return;
    const success = await database.deleteChatThread(email);
    if (success) {
      onUpdateChatThreads(prev => prev.filter(t => t.userEmail !== email));
      if (selectedThreadEmail === email) setSelectedThreadEmail(null);
    }
  };

  const handleUploadRealArt = async () => {
    if (editingArt && !pendingFile) {
      setIsUploading(true);
      const updatedArt = { ...editingArt, ...newArt } as ArtItem;
      const success = await database.updateArtMetadata(updatedArt);
      if (success) {
        onUpdateArts(prev => prev.map(a => a.id === updatedArt.id ? updatedArt : a));
        resetForm();
      }
      setIsUploading(false);
      return;
    }

    if (!pendingFile || !newArt.title?.pt) {
      alert("Título (PT) e Arquivo são obrigatórios.");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', pendingFile);
    formData.append('title_pt', newArt.title.pt);
    formData.append('title_en', newArt.title.en || newArt.title.pt);
    formData.append('category', newArt.category || 'Animated');
    formData.append('subcategory', newArt.subcategory || '');
    formData.append('art_type', newArt.artType || 'Single Artwork');
    formData.append('price', (newArt.price || 0).toString());
    formData.append('desc_pt', newArt.description?.pt || '');
    formData.append('desc_en', newArt.description?.en || '');
    formData.append('is_animated', (!!newArt.isAnimated).toString());
    formData.append('steam_background_url', newArt.steamBackgroundUrl || '');
    formData.append('not_for_sale', (!!newArt.notForSale).toString());

    const result = await database.uploadNewArt(formData);
    if (result.success && result.item) {
      if (editingArt) {
        await database.deleteArt(editingArt.id);
        onUpdateArts(prev => [result.item!, ...prev.filter(a => a.id !== editingArt.id)]);
      } else {
        onUpdateArts(prev => [result.item!, ...prev]);
      }
      resetForm();
    } else {
      alert("Erro no upload: " + result.error);
    }
    setIsUploading(false);
  };

  const handleSaveCategoryCover = async () => {
    if (!editingCategory) return;
    setIsSyncing(true);
    let finalUrl = catCoverPreview || '';
    if (catPendingFile) {
      const uploadRes = await database.uploadCategoryCover(editingCategory, catPendingFile);
      if (uploadRes.success && uploadRes.url) {
        finalUrl = uploadRes.url;
      } else {
        alert("Erro no upload da capa: " + (uploadRes.error || "Erro desconhecido"));
        setIsSyncing(false);
        return;
      }
    }
    const updatedCovers = { ...categoryCovers, [editingCategory]: finalUrl };
    onUpdateCategoryCovers(updatedCovers);
    const success = await database.saveCategoryCovers(updatedCovers);
    if (success) {
      setEditingCategory(null);
      setCatPendingFile(null);
      setCatCoverPreview(null);
    }
    setIsSyncing(false);
  };

  const resetForm = () => {
    setIsAddingArt(false);
    setEditingArt(null);
    setPendingFile(null);
    setPreviewUrl(null);
    setNewArt({ 
      title: { pt: '', en: '' }, 
      category: ArtCategory.ANIMATED, 
      artType: 'Single Artwork',
      price: 15.00, 
      isAnimated: true,
      steamBackgroundUrl: '',
      notForSale: false
    });
  };

  const maxVisits = Math.max(...analytics.map(a => a.uniqueCount || a.count), 1);

  return (
    <div className="fixed inset-0 z-[300] bg-[#050505] overflow-y-auto pb-20 custom-scrollbar">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex justify-between items-center mb-16 border-b border-white/5 pb-10">
          <div className="flex items-center gap-4">
            <div className="bg-white p-2">
              <Flower className="w-6 h-6 text-black" />
            </div>
            <h2 className="text-2xl font-black text-white uppercase tracking-tighter">Santuário <span className="text-[#ff9eb5]">Admin</span></h2>
          </div>
          <div className="flex bg-zinc-900 p-1 flex-wrap">
            {['gallery', 'orders', 'chats', 'categories', 'commissions', 'reviews', 'stats', 'settings', 'footer', 'faq'].map((tab) => (
              <button 
                key={tab} 
                onClick={() => setActiveTab(tab as any)} 
                className={`px-6 py-3 text-[10px] font-black uppercase transition-all relative ${activeTab === tab ? 'bg-[#ff9eb5] text-white' : 'text-zinc-500 hover:text-white'}`}
              >
                {tab}
                {tab === 'orders' && notifications && notifications.orders > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] px-1.5 py-0.5 rounded-full animate-bounce">
                    {notifications.orders}
                  </span>
                )}
                {tab === 'chats' && notifications && notifications.chats > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] px-1.5 py-0.5 rounded-full animate-bounce">
                    {notifications.chats}
                  </span>
                )}
              </button>
            ))}
          </div>
          <button onClick={onClose} className="p-4 bg-zinc-900 border border-zinc-800 text-zinc-500 hover:text-white"><X className="w-7 h-7" /></button>
        </div>

        {activeTab === 'commissions' && (
          <div className="space-y-12 animate-in fade-in duration-700">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h3 className="text-white font-black uppercase text-sm tracking-widest">Capas de Custom Art</h3>
                <p className="text-zinc-600 text-[8px] font-black uppercase tracking-[0.4em]">Edite a demonstração visual das animações</p>
              </div>
              <button 
                onClick={refreshCommissionTiers}
                className="p-3 bg-zinc-950 text-[#ff9eb5] hover:bg-[#ff9eb5] hover:text-white transition-all flex items-center gap-2 text-[10px] font-black uppercase"
              >
                <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} /> Atualizar
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {commissionTiers.map(tier => (
                <div key={tier.id} className="bg-zinc-900/40 border border-white/5 p-6 space-y-6 group hover:border-[#ff9eb5]/30 transition-all">
                  <div className="aspect-[9/14] bg-black overflow-hidden relative">
                    {tierPreview && editingTier === tier.id ? (
                      isVideoUrl(tierPreview) ? (
                        <video src={tierPreview} autoPlay loop muted className="w-full h-full object-cover" />
                      ) : (
                        <img src={tierPreview} className="w-full h-full object-cover" />
                      )
                    ) : (
                      isVideoUrl(tier.demoUrl) ? (
                        <video src={tier.demoUrl} autoPlay loop muted className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity" />
                      ) : (
                        <img src={tier.demoUrl} className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity" />
                      )
                    )}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <h4 className="text-white font-black uppercase text-xs tracking-widest">{tier.title}</h4>
                    </div>
                  </div>

                  {editingTier === tier.id ? (
                    <div className="space-y-4 animate-in slide-in-from-top-2 duration-300">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest">URL da Demonstração</label>
                          <input 
                            type="text" 
                            placeholder="https://..." 
                            value={tierPreview || ''} 
                            onChange={(e) => setTierPreview(e.target.value)} 
                            className="w-full bg-black border border-zinc-800 p-3 text-white text-[10px] outline-none focus:border-[#ff9eb5]" 
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest">Preço Inicial ($)</label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-600" />
                            <input 
                              type="number" 
                              step="0.01"
                              value={commissionTiers.find(t => t.id === tier.id)?.price || 0} 
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                setCommissionTiers(prev => prev.map(t => t.id === tier.id ? { ...t, price: val } : t));
                              }} 
                              className="w-full bg-black border border-zinc-800 p-3 pl-8 text-white text-[10px] outline-none focus:border-[#ff9eb5]" 
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest">Preço Máximo ($ - Opcional)</label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-600" />
                            <input 
                              type="number" 
                              step="0.01"
                              placeholder="Fim da faixa"
                              value={commissionTiers.find(t => t.id === tier.id)?.maxPrice || ''} 
                              onChange={(e) => {
                                const val = e.target.value === '' ? undefined : parseFloat(e.target.value);
                                setCommissionTiers(prev => prev.map(t => t.id === tier.id ? { ...t, maxPrice: val } : t));
                              }} 
                              className="w-full bg-black border border-zinc-800 p-3 pl-8 text-white text-[10px] outline-none focus:border-[#ff9eb5]" 
                            />
                          </div>
                        </div>
                        <div className="space-y-2 col-span-full">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest">Descrição do Plano</label>
                          <textarea 
                            rows={2}
                            value={commissionTiers.find(t => t.id === tier.id)?.description || ''} 
                            onChange={(e) => {
                              const val = e.target.value;
                              setCommissionTiers(prev => prev.map(t => t.id === tier.id ? { ...t, description: val } : t));
                            }} 
                            className="w-full bg-black border border-zinc-800 p-3 text-white text-[10px] outline-none focus:border-[#ff9eb5] resize-none" 
                            placeholder="Descreva este plano..."
                          />
                        </div>
                        <div className="space-y-2 col-span-full">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest">Link de Pagamento Stripe - USD/Geral (Opcional)</label>
                          <div className="relative">
                            <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-600" />
                            <input 
                              type="text" 
                              placeholder="https://buy.stripe.com/..." 
                              value={commissionTiers.find(t => t.id === tier.id)?.stripeUrl || ''} 
                              onChange={(e) => {
                                const val = e.target.value;
                                setCommissionTiers(prev => prev.map(t => t.id === tier.id ? { ...t, stripeUrl: val } : t));
                              }} 
                              className="w-full bg-black border border-zinc-800 p-3 pl-8 text-white text-[10px] outline-none focus:border-[#ff9eb5]" 
                            />
                          </div>
                        </div>
                        <div className="space-y-2 col-span-full">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest">Link de Pagamento Stripe - BRL/Brasil (Opcional)</label>
                          <div className="relative">
                            <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-600" />
                            <input 
                              type="text" 
                              placeholder="https://buy.stripe.com/..." 
                              value={commissionTiers.find(t => t.id === tier.id)?.stripeUrlBr || ''} 
                              onChange={(e) => {
                                const val = e.target.value;
                                setCommissionTiers(prev => prev.map(t => t.id === tier.id ? { ...t, stripeUrlBr: val } : t));
                              }} 
                              className="w-full bg-black border border-zinc-800 p-3 pl-8 text-white text-[10px] outline-none focus:border-[#ff9eb5]" 
                            />
                          </div>
                        </div>
                      </div>
                      <input 
                        ref={tierFileInputRef} 
                        type="file" 
                        className="hidden" 
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setTierPendingFile(file);
                            setTierPreview(URL.createObjectURL(file));
                          }
                        }} 
                        accept="image/*,video/mp4" 
                      />
                      <div className="flex gap-2">
                        <button 
                          onClick={() => tierFileInputRef.current?.click()} 
                          className="flex-1 bg-zinc-800 text-white py-3 text-[9px] font-black uppercase hover:bg-zinc-700 transition-all flex items-center justify-center gap-2"
                        >
                          <Upload className="w-3 h-3" /> Upload
                        </button>
                        <button 
                          onClick={async () => {
                            setIsSyncing(true);
                            let finalUrl = tierPreview || tier.demoUrl;
                            if (tierPendingFile) {
                              const uploadRes = await database.uploadCommissionTierCover(tier.id, tierPendingFile);
                              if (uploadRes.success) {
                                finalUrl = uploadRes.url!;
                              } else {
                                alert(uploadRes.error);
                                setIsSyncing(false);
                                return;
                              }
                            }
                            
                            const currentTier = commissionTiers.find(t => t.id === tier.id);
                            const updatedTiers = commissionTiers.map(t => t.id === tier.id ? { 
                              ...t, 
                              demoUrl: finalUrl, 
                              price: currentTier?.price || 0,
                              maxPrice: currentTier?.maxPrice
                            } : t);
                            const saveRes = await database.saveCommissionTiers(updatedTiers);
                            if (saveRes.success) {
                              setCommissionTiers(updatedTiers);
                              onUpdateCommissionTiers(updatedTiers);
                              setEditingTier(null);
                              setTierPendingFile(null);
                              setTierPreview(null);
                            } else {
                              alert(`Erro ao salvar no servidor: ${saveRes.error || 'Desconhecido'}`);
                            }
                            setIsSyncing(false);
                          }} 
                          disabled={isSyncing} 
                          className="flex-1 bg-[#ff9eb5] text-white py-3 text-[9px] font-black uppercase hover:bg-white hover:text-[#ff9eb5] transition-all flex items-center justify-center gap-2"
                        >
                          {isSyncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Salvar
                        </button>
                        <button 
                          onClick={() => { setEditingTier(null); setTierPendingFile(null); setTierPreview(null); }} 
                          className="px-4 bg-zinc-950 text-zinc-500 py-3 text-[9px] font-black uppercase hover:text-white"
                        >
                          X
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button 
                      onClick={() => { setEditingTier(tier.id); setTierPreview(tier.demoUrl); }} 
                      className="w-full py-4 border border-zinc-800 text-zinc-500 font-black uppercase text-[10px] hover:text-white hover:border-[#ff9eb5] transition-all flex items-center justify-center gap-2"
                    >
                      <Edit2 className="w-3 h-3" /> Editar Plano
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="space-y-12 animate-in fade-in duration-700">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-zinc-900/40 border border-white/5 p-8 backdrop-blur-md">
                  <div className="flex justify-between items-center mb-4">
                    <Users className="w-6 h-6 text-[#ff9eb5]" />
                    <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Visitantes Únicos Hoje</span>
                  </div>
                  <div className="text-4xl font-black text-white">{analytics[analytics.length - 1]?.uniqueCount || analytics[analytics.length - 1]?.count || 0}</div>
                  <p className="text-[8px] text-zinc-600 font-black uppercase mt-2 tracking-widest">Membros únicos detectados</p>
               </div>
               <div className="bg-zinc-900/40 border border-white/5 p-8 backdrop-blur-md">
                  <div className="flex justify-between items-center mb-4">
                    <TrendingUp className="w-6 h-6 text-green-500" />
                    <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Total de Cliques</span>
                  </div>
                  <div className="text-4xl font-black text-white">{analytics[analytics.length - 1]?.totalCount || 0}</div>
                  <p className="text-[8px] text-zinc-600 font-black uppercase mt-2 tracking-widest">Total de carregamentos (F5)</p>
               </div>
               <div className="bg-zinc-900/40 border border-white/5 p-8 backdrop-blur-md">
                  <div className="flex justify-between items-center mb-4">
                    <BarChart3 className="w-6 h-6 text-blue-500" />
                    <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Únicos (30 Dias)</span>
                  </div>
                  <div className="text-4xl font-black text-white">{analytics.reduce((acc, curr) => acc + (curr.uniqueCount || curr.count), 0)}</div>
                  <p className="text-[8px] text-zinc-600 font-black uppercase mt-2 tracking-widest">Soma de visitantes distintos</p>
               </div>
            </div>

            <div className="bg-zinc-900/40 border border-white/5 p-12 backdrop-blur-md">
               <div className="flex justify-between items-center mb-16">
                  <h3 className="text-white font-black uppercase text-sm tracking-widest">Fluxo de Presença</h3>
                  <button onClick={refreshAnalytics} className="p-3 bg-zinc-950 text-[#ff9eb5] hover:bg-[#ff9eb5] hover:text-white transition-all"><RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} /></button>
               </div>
               
               <div className="h-64 flex items-end gap-2 md:gap-4 overflow-x-auto pb-4 custom-scrollbar">
                  {analytics.map((day, idx) => {
                    const displayCount = day.uniqueCount || day.count;
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center group min-w-[30px]">
                         <div className="text-[8px] font-black text-white mb-2 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center">
                           <span>U: {displayCount}</span>
                           <span className="text-zinc-500">T: {day.totalCount}</span>
                         </div>
                         <div 
                          className="w-full bg-[#ff9eb5]/10 border-t border-x border-[#ff9eb5]/30 group-hover:bg-[#ff9eb5] transition-all relative overflow-hidden" 
                          style={{ height: `${(displayCount / maxVisits) * 100}%`, minHeight: '4px' }}
                         >
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                         </div>
                         <div className="text-[7px] font-black text-zinc-600 uppercase mt-4 rotate-45 md:rotate-0 text-center whitespace-nowrap">
                           {day.date.split('-').slice(1).reverse().join('/')}
                         </div>
                      </div>
                    );
                  })}
               </div>
            </div>
          </div>
        )}

        {activeTab === 'reviews' && (
          <div className="space-y-12 animate-in fade-in duration-700">
            <div className="bg-zinc-900/40 border border-white/5 p-8 md:p-12 backdrop-blur-md">
              <div className="flex justify-between items-center mb-10 border-b border-white/5 pb-6">
                <div className="flex items-center gap-4">
                  <Star className="w-6 h-6 text-[#ff9eb5] fill-[#ff9eb5]" />
                  <div>
                    <h3 className="text-white font-black uppercase text-sm tracking-widest">{language === 'pt' ? 'Gerenciamento de Avaliações' : 'Reviews & Feedback Management'}</h3>
                    <p className="text-zinc-600 text-[8px] font-black uppercase tracking-[0.4em]">{language === 'pt' ? 'Responda ou exclua depoimentos de clientes reais' : 'Reply to or delete verified customer feedback'}</p>
                  </div>
                </div>
                <div className="bg-zinc-950 px-4 py-2 border border-white/5 font-mono text-xs text-zinc-400">
                  <span className="text-[#ff9eb5] font-black">{cleanComments.length}</span> {language === 'pt' ? 'avaliações no total' : 'total reviews'}
                </div>
              </div>

              {cleanComments.length === 0 ? (
                <div className="py-24 text-center border border-dashed border-white/5">
                  <Star className="w-12 h-12 text-zinc-700 mx-auto mb-4 opacity-20" />
                  <p className="text-zinc-500 font-bold uppercase tracking-wider text-[10px]">
                    {language === 'pt' ? 'Nenhuma avaliação registrada no acervo.' : 'No registered reviews in cache.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-8">
                  {cleanComments.map((comment) => (
                    <div key={comment.id} className="bg-black/40 border border-white/5 p-6 md:p-8 space-y-6 relative group transition-all hover:border-zinc-800">
                      
                      {/* Top bar of comment */}
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-white/5 pb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-zinc-900 border border-zinc-800 flex items-center justify-center font-black text-xs text-[#ff9eb5]">
                            {comment.userName.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-white font-black uppercase text-xs tracking-wider">{comment.userName}</span>
                              <span className="text-zinc-500 font-mono text-[9px]">({comment.userEmail})</span>
                              {comment.verified && (
                                <span className="text-[7px] font-black text-[#ff9eb5] uppercase bg-[#ff9eb5]/10 px-2 py-0.5 rounded-full border border-[#ff9eb5]/10">
                                  {language === 'pt' ? 'Cliente Real' : 'Real Customer'}
                                </span>
                              )}
                            </div>
                            <span className="text-[9px] text-zinc-600 font-mono block mt-1">{comment.date} | ID: {comment.id}</span>
                          </div>
                        </div>

                        {/* Stars */}
                        <div className="flex items-center gap-1 bg-black/80 px-3 py-1.5 border border-white/5">
                          {[1, 2, 3, 4, 5].map((starValue) => (
                            <Star 
                              key={starValue} 
                              className={`w-3.5 h-3.5 ${starValue <= (comment.rating || 5) ? 'text-[#ff9eb5] fill-[#ff9eb5]' : 'text-zinc-800'}`} 
                            />
                          ))}
                        </div>
                      </div>

                      {/* Comment text */}
                      <div className="text-sm font-medium text-zinc-300 italic py-2 pl-4 border-l-2 border-zinc-900">
                        "{comment.text}"
                      </div>

                      {/* Existing Reply */}
                      {comment.reply ? (
                        <div className="mt-4 bg-[#ff9eb5]/5 border border-[#ff9eb5]/10 p-5 space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-[8px] font-black uppercase tracking-widest text-[#ff9eb5] flex items-center gap-2">
                              <span>★</span> {language === 'pt' ? 'Sua Resposta Oficial' : 'Your Official Reply'}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleDeleteReply(comment.id)}
                              className="text-red-400 hover:text-red-300 font-black text-[9px] uppercase tracking-wider flex items-center gap-1 transition-colors hover:underline"
                            >
                              <Trash2 className="w-3 h-3" /> {language === 'pt' ? 'Deletar Resposta' : 'Delete Reply'}
                            </button>
                          </div>
                          <p className="text-xs text-zinc-400 leading-relaxed font-medium">
                            {comment.reply}
                          </p>
                        </div>
                      ) : (
                        /* Unreplied Form */
                        <div className="space-y-3 pt-2">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest block font-mono">
                            {language === 'pt' ? 'Responder Avaliação' : 'Reply to Review'}
                          </label>
                          <div className="flex gap-4">
                            <input 
                              type="text"
                              value={commentReplyTexts[comment.id] || ''}
                              onChange={(e) => setCommentReplyTexts(prev => ({ ...prev, [comment.id]: e.target.value }))}
                              placeholder={language === 'pt' ? 'Escreva uma resposta de agradecimento ou esclarecimento...' : 'Write an official reply of gratitude or explanation...'}
                              className="flex-1 bg-black border border-zinc-800 p-3 text-xs text-white outline-none focus:border-[#ff9eb5] transition-all"
                            />
                            <button
                              type="button"
                              onClick={() => handleSaveReply(comment.id, commentReplyTexts[comment.id] || '')}
                              disabled={!(commentReplyTexts[comment.id] || '').trim()}
                              className="px-6 bg-white hover:bg-[#ff9eb5] text-black hover:text-white font-black text-[10px] uppercase tracking-wider transition-colors disabled:opacity-20 whitespace-nowrap"
                            >
                              {language === 'pt' ? 'Responder' : 'Reply'}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Review delete action */}
                      <div className="flex justify-end pt-4 border-t border-white/5">
                        <button
                          type="button"
                          onClick={() => handleDeleteComment(comment.id)}
                          className="bg-red-500/10 border border-red-500/20 hover:bg-red-500 hover:text-white text-red-500 font-black text-[9px] uppercase tracking-wider py-2 px-4 transition-all"
                        >
                          {language === 'pt' ? 'Deletar Avaliação Inteira' : 'Delete Entire Review'}
                        </button>
                      </div>

                    </div>
                  ))}
                </div>
              )}

            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-12 animate-in fade-in duration-700">
            <div className="bg-zinc-900/40 border border-white/5 p-8 md:p-12 backdrop-blur-md">
              <div className="flex items-center gap-4 mb-10 border-b border-white/5 pb-6">
                <Palette className="w-6 h-6 text-[#ff9eb5]" />
                <h3 className="text-white font-black uppercase text-sm tracking-widest">Personalização do Santuário</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div className="space-y-6">
                  <div className="flex justify-between items-end">
                    <div>
                      <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest block mb-1">Background da Home</label>
                      <p className="text-[8px] text-zinc-600 font-black uppercase tracking-widest">Imagem de fundo principal da aplicação</p>
                    </div>
                  </div>

                  <div className="aspect-video bg-black border border-white/5 overflow-hidden relative group">
                    {isVideoUrl(catCoverPreview || categoryCovers['HOME_BG']) ? (
                      <video src={catCoverPreview || categoryCovers['HOME_BG']} autoPlay loop muted className="w-full h-full object-cover opacity-50 group-hover:opacity-100 transition-opacity" />
                    ) : (
                      <img src={catCoverPreview || categoryCovers['HOME_BG'] || MASTER_FALLBACK_IMAGE} className="w-full h-full object-cover opacity-50 group-hover:opacity-100 transition-opacity" />
                    )}
                    
                    {editingCategory !== 'HOME_BG' && (
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all bg-black/40">
                         <button 
                          onClick={() => { setEditingCategory('HOME_BG'); setCatCoverPreview(categoryCovers['HOME_BG']); }}
                          className="bg-white text-black px-8 py-4 text-[10px] font-black uppercase hover:bg-[#ff9eb5] hover:text-white transition-all shadow-2xl"
                         >
                           Alterar Background
                         </button>
                      </div>
                    )}
                  </div>

                  {editingCategory === 'HOME_BG' && (
                    <div className="space-y-4 animate-in slide-in-from-top-2 duration-300 bg-black/40 p-6 border border-[#ff9eb5]/20">
                       <div className="flex flex-col gap-2">
                          <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest">URL da Imagem/Vídeo</label>
                          <input type="text" placeholder="https://..." value={catCoverPreview || ''} onChange={(e) => setCatCoverPreview(e.target.value)} className="w-full bg-black border border-zinc-800 p-3 text-white text-[10px] outline-none focus:border-[#ff9eb5]" />
                       </div>
                       <div className="flex gap-2">
                          <button onClick={() => catFileInputRef.current?.click()} className="flex-1 bg-zinc-800 text-white py-3 text-[9px] font-black uppercase hover:bg-zinc-700 transition-all flex items-center justify-center gap-2"><Upload className="w-3 h-3" /> Upload Arquivo</button>
                          <button onClick={handleSaveCategoryCover} disabled={isSyncing} className="flex-1 bg-[#ff9eb5] text-white py-3 text-[9px] font-black uppercase hover:bg-white hover:text-[#ff9eb5] transition-all flex items-center justify-center gap-2">{isSyncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Salvar Alteração</button>
                          <button onClick={() => { setEditingCategory(null); setCatPendingFile(null); setCatCoverPreview(null); }} className="px-4 bg-zinc-950 text-zinc-500 py-3 text-[9px] font-black uppercase hover:text-white">Cancelar</button>
                       </div>
                    </div>
                  )}
                  
                  <div className="p-4 bg-black/40 border border-white/5">
                    <p className="text-[8px] text-zinc-500 font-black uppercase leading-relaxed">
                      Dica: Use imagens de alta resolução (1920x1080) para melhor resultado. 
                      O sistema aplica automaticamente um filtro de escurecimento para garantir a legibilidade.
                    </p>
                  </div>
                </div>

                <div className="space-y-8">
                   <div className="p-8 bg-zinc-950 border border-white/5 space-y-6">
                      <h4 className="text-white font-black uppercase text-[10px] tracking-widest flex items-center gap-2">
                        <Type className="w-4 h-4 text-[#ff9eb5]" /> Textos de Introdução (Custom Art)
                      </h4>
                      
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest block">Português</label>
                          <textarea 
                            value={siteSettingsState.customRequestIntro_pt} 
                            onChange={(e) => setSiteSettingsState(prev => ({ ...prev, customRequestIntro_pt: e.target.value }))}
                            className="w-full bg-black border border-zinc-800 p-4 text-white text-[10px] outline-none focus:border-[#ff9eb5] resize-none h-24"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest block">Inglês</label>
                          <textarea 
                            value={siteSettingsState.customRequestIntro_en} 
                            onChange={(e) => setSiteSettingsState(prev => ({ ...prev, customRequestIntro_en: e.target.value }))}
                            className="w-full bg-black border border-zinc-800 p-4 text-white text-[10px] outline-none focus:border-[#ff9eb5] resize-none h-24"
                          />
                        </div>
                        <button 
                          onClick={async () => {
                            setIsSyncing(true);
                            const saveRes = await database.saveSettings(siteSettingsState);
                            if (saveRes.success) {
                              onUpdateSiteSettings(siteSettingsState);
                              alert("Textos updated!");
                            } else {
                              alert(`Erro: ${saveRes.message || 'Falha ao salvar.'}`);
                            }
                            setIsSyncing(false);
                          }}
                          disabled={isSyncing}
                          className="w-full bg-white hover:bg-[#ff9eb5] text-black hover:text-white py-3 font-black uppercase text-[10px] flex items-center justify-center gap-2"
                        >
                          {isSyncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Salvar Textos
                        </button>
                      </div>
                   </div>

                   <div className="p-8 bg-zinc-950 border border-white/5 space-y-4">
                      <h4 className="text-white font-black uppercase text-[10px] tracking-widest flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-green-500" /> Status do Sistema
                      </h4>
                      <div className="space-y-2">
                         <div className="flex justify-between text-[9px] font-black uppercase">
                            <span className="text-zinc-500">Versão dos Dados</span>
                            <span className="text-white">v2.0.4</span>
                         </div>
                         <div className="flex justify-between text-[9px] font-black uppercase">
                            <span className="text-zinc-500">Servidor</span>
                            <span className="text-green-500">Online (Hostinger)</span>
                         </div>
                         <div className="flex justify-between text-[9px] font-black uppercase">
                            <span className="text-zinc-500">Cache Local</span>
                            <span className="text-[#ff9eb5]">Ativo</span>
                         </div>
                      </div>
                   </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'footer' && (
          <div className="space-y-12 animate-in fade-in duration-700">
            <div className="bg-zinc-900/40 border border-white/5 p-8 md:p-12 backdrop-blur-md">
              <div className="flex items-center gap-4 mb-10 border-b border-white/5 pb-6">
                <LinkIcon className="w-6 h-6 text-[#ff9eb5]" />
                <h3 className="text-white font-black uppercase text-sm tracking-widest">Links do Rodapé</h3>
              </div>

              <div className="max-w-2xl space-y-8">
                <div className="p-8 bg-zinc-950 border border-white/5 space-y-6">
                  <h4 className="text-white font-black uppercase text-[10px] tracking-widest flex items-center gap-2">
                    <Settings className="w-4 h-4 text-[#ff9eb5]" /> Configurar Redes Sociais no Rodapé
                  </h4>
                  
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest block font-sans">Disponibilidade dos Botões</label>
                      <p className="text-[8px] text-zinc-650 font-black uppercase leading-relaxed font-sans">
                        Os links configurados abaixo aparecerão como botões clicáveis no rodapé da página inicial (Home), logo abaixo da seção de avaliações dos clientes (Client Reviews).
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block flex items-center gap-2">
                        <span className="w-2 h-2 bg-[#ff5a5f] rounded-full animate-pulse" /> DeviantArt (URL)
                      </label>
                      <input 
                        type="text" 
                        placeholder="https://www.deviantart.com/seu-perfil" 
                        value={siteSettingsState.deviantartUrl || ''} 
                        onChange={(e) => setSiteSettingsState(prev => ({ ...prev, deviantartUrl: e.target.value }))}
                        className="w-full bg-black border border-zinc-800 p-4 text-white text-[11px] outline-none focus:border-[#ff9eb5] font-mono"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block flex items-center gap-2">
                        <span className="w-2 h-2 bg-[#229ED9] rounded-full animate-pulse" /> Telegram (URL)
                      </label>
                      <input 
                        type="text" 
                        placeholder="https://t.me/seu-usuario ou canal" 
                        value={siteSettingsState.telegramUrl || ''} 
                        onChange={(e) => setSiteSettingsState(prev => ({ ...prev, telegramUrl: e.target.value }))}
                        className="w-full bg-black border border-zinc-800 p-4 text-white text-[11px] outline-none focus:border-[#ff9eb5] font-mono"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block flex items-center gap-2">
                        <span className="w-2 h-2 bg-[#5865F2] rounded-full animate-pulse" /> Discord (URL)
                      </label>
                      <input 
                        type="text" 
                        placeholder="https://discord.gg/seu-convite" 
                        value={siteSettingsState.discordUrl || ''} 
                        onChange={(e) => setSiteSettingsState(prev => ({ ...prev, discordUrl: e.target.value }))}
                        className="w-full bg-black border border-zinc-800 p-4 text-white text-[11px] outline-none focus:border-[#ff9eb5] font-mono"
                      />
                    </div>

                    <button 
                      onClick={async () => {
                        setIsSyncing(true);
                        const saveRes = await database.saveSettings(siteSettingsState);
                        if (saveRes.success) {
                          onUpdateSiteSettings(siteSettingsState);
                          alert("Links do rodapé atualizados com sucesso!");
                        } else {
                          alert(`Erro: ${saveRes.message || 'Falha ao salvar.'}`);
                        }
                        setIsSyncing(false);
                      }}
                      disabled={isSyncing}
                      className="w-full bg-white hover:bg-[#ff9eb5] text-black hover:text-white py-4 font-black uppercase text-[10px] flex items-center justify-center gap-2 tracking-widest transition-all mt-4 border-b-4 border-zinc-300 hover:border-[#ff9eb5] active:scale-95"
                    >
                      {isSyncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Salvar Configurações de Rodapé
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'faq' && (
          <div className="space-y-12 animate-in fade-in duration-700">
            <div className="bg-zinc-900/40 border border-white/5 p-8 md:p-12 backdrop-blur-md">
              <div className="flex items-center gap-4 mb-10 border-b border-white/5 pb-6">
                <HelpCircle className="w-6 h-6 text-[#ff9eb5]" />
                <h3 className="text-white font-black uppercase text-sm tracking-widest">Gerenciar F.A.Q.</h3>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Add New FAQ Form */}
                <div className="space-y-6">
                  <div className="p-8 bg-zinc-950 border border-white/5 space-y-6">
                    <h4 className="text-white font-black uppercase text-[10px] tracking-widest flex items-center gap-2">
                      <Plus className="w-4 h-4 text-[#ff9eb5]" /> Adicionar Nova Pergunta
                    </h4>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block">Pergunta (Português)</label>
                        <input 
                          type="text"
                          id="new-faq-q-pt"
                          placeholder="Ex: Como funciona o prazo de entrega?"
                          className="w-full bg-black border border-zinc-800 p-4 text-white text-[11px] outline-none focus:border-[#ff9eb5] font-sans"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block">Pergunta (Inglês)</label>
                        <input 
                          type="text"
                          id="new-faq-q-en"
                          placeholder="Ex: How long does delivery take?"
                          className="w-full bg-black border border-zinc-800 p-4 text-white text-[11px] outline-none focus:border-[#ff9eb5] font-sans"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block">Resposta (Português)</label>
                        <textarea 
                          id="new-faq-a-pt"
                          rows={3}
                          placeholder="Ex: Nossos prazos variam de 3 a 5 dias..."
                          className="w-full bg-black border border-zinc-800 p-4 text-white text-[11px] outline-none focus:border-[#ff9eb5] font-sans resize-none"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block">Resposta (Inglês)</label>
                        <textarea 
                          id="new-faq-a-en"
                          rows={3}
                          placeholder="Ex: Delivery takes between 3 to 5 business days..."
                          className="w-full bg-black border border-zinc-800 p-4 text-white text-[11px] outline-none focus:border-[#ff9eb5] font-sans resize-none"
                        />
                      </div>

                      <button 
                        type="button"
                        onClick={() => {
                          const qPtEl = document.getElementById('new-faq-q-pt') as HTMLInputElement;
                          const qEnEl = document.getElementById('new-faq-q-en') as HTMLInputElement;
                          const aPtEl = document.getElementById('new-faq-a-pt') as HTMLTextAreaElement;
                          const aEnEl = document.getElementById('new-faq-a-en') as HTMLTextAreaElement;

                          if (!qPtEl?.value || !qEnEl?.value || !aPtEl?.value || !aEnEl?.value) {
                            alert("Por favor, preencha todos os campos da nova pergunta!");
                            return;
                          }

                          const newFaq = {
                            id: 'faq_' + Date.now(),
                            question_pt: qPtEl.value,
                            question_en: qEnEl.value,
                            answer_pt: aPtEl.value,
                            answer_en: aEnEl.value
                          };

                          const currentFaqs = siteSettingsState.faqs || [];
                          const updatedFaqs = [...currentFaqs, newFaq];

                          setSiteSettingsState(prev => ({
                            ...prev,
                            faqs: updatedFaqs
                          }));

                          // Reset inputs
                          qPtEl.value = '';
                          qEnEl.value = '';
                          aPtEl.value = '';
                          aEnEl.value = '';
                          
                          alert("Pergunta adicionada à lista temporária. Lembre-se de clicar em salvar no final!");
                        }}
                        className="w-full bg-white hover:bg-[#ff9eb5] text-black hover:text-white py-4 font-black uppercase text-[10px] tracking-widest transition-all border-b-2 border-zinc-300 hover:border-[#ff9eb5] active:scale-95"
                      >
                        Incluir Pergunta na Lista
                      </button>
                    </div>
                  </div>
                </div>

                {/* FAQ List and Save */}
                <div className="space-y-6">
                  <div className="p-8 bg-zinc-950 border border-white/5 space-y-6">
                    <div className="flex items-center justify-between">
                      <h4 className="text-white font-black uppercase text-[10px] tracking-widest flex items-center gap-2">
                        <HelpCircle className="w-4 h-4 text-[#ff9eb5]" /> Perguntas Existentes ({siteSettingsState.faqs?.length || 0})
                      </h4>
                      {(!siteSettingsState.faqs || siteSettingsState.faqs.length === 0) && (
                        <button
                          type="button"
                          onClick={() => {
                            const defaultFaqs = [
                              {
                                id: 'faq_1',
                                question_pt: 'Como funcionam os pedidos de arte personalizada?',
                                question_en: 'How do custom art orders work?',
                                answer_pt: 'Para fazer um pedido, basta criar uma conta ou fazer o login, navegar pelas abas do site e selecionar a categoria apropriada. Você pode escolher dimensões, enviar referências de fundo, customizar textos e adicionar detalhes adicionais. O progresso pode ser acompanhado diretamente pelo seu painel de usuário.',
                                answer_en: 'To place an order, simply create an account or log in, browse the categories, and request custom designs. You can specify dimensions, configure background images, add custom texts, and more. All steps of your order can be tracked in real-time in your client dashboard.'
                              },
                              {
                                id: 'faq_2',
                                question_pt: 'Quais são os formatos de entrega?',
                                question_en: 'What are the delivery formats?',
                                answer_pt: 'Nossas artes para a Steam são otimizadas para atender perfeitamente aos padrões da plataforma. Entregamos arquivos GIF de alta qualidade para artes animadas e PNGs em alta definição para artes estáticas, além de links e instruções para o upload perfeito sem perdas de qualidade.',
                                answer_en: 'Our Steam artworks are optimized to fully comply with the platform standards. We deliver high-quality GIF files for animated backgrounds and high-definition PNGs for static artworks, alongside instructions for an absolute lossless upload.'
                              },
                              {
                                id: 'faq_3',
                                question_pt: 'Quais métodos de pagamento são aceitos?',
                                question_en: 'What payment methods are accepted?',
                                answer_pt: 'Oferecemos opções modernas para transações rápidas e integradas de acordo com as especificações do Santuário. Todos os detalhes de orçamento e acréscimos adicionais para customizações extras podem ser sanados através do nosso canal direto no chat ou contatos de suporte.',
                                answer_en: 'We support modern payment methods to initiate projects instantly. Detailed cost estimates and additional customization fees can be comfortably negotiated or discussed in detail through our real-time messenger chat or Support.'
                              }
                            ];
                            setSiteSettingsState(prev => ({ ...prev, faqs: defaultFaqs }));
                          }}
                          className="text-[8px] bg-zinc-900 border border-zinc-800 text-[#ff9eb5] hover:bg-[#ff9eb5]/10 px-3 py-1.5 font-black uppercase tracking-widest transition-all"
                        >
                          Carregar Padrão
                        </button>
                      )}
                    </div>

                    <div className="space-y-4 max-h-[350px] overflow-y-auto pr-2 custom-scrollbar">
                      {(!siteSettingsState.faqs || siteSettingsState.faqs.length === 0) ? (
                        <div className="text-center py-10 border border-dashed border-zinc-900">
                          <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wide">Nenhuma pergunta na lista.</p>
                          <p className="text-[8px] text-zinc-650 font-medium uppercase mt-1">Carregue o padrão ou adicione perguntas ao lado.</p>
                        </div>
                      ) : (
                        siteSettingsState.faqs.map((faq, idx) => (
                          <div key={faq.id} className="p-4 bg-black border border-zinc-900 space-y-4 relative group">
                            <div className="flex items-start justify-between gap-4 border-b border-zinc-900 pb-3">
                              <span className="text-[9px] font-mono font-bold text-[#ff9eb5] bg-[#ff9eb5]/10 px-2 py-0.5">#{idx + 1}</span>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (idx === 0) return;
                                    const items = [...(siteSettingsState.faqs || [])];
                                    const temp = items[idx];
                                    items[idx] = items[idx - 1];
                                    items[idx - 1] = temp;
                                    setSiteSettingsState(prev => ({ ...prev, faqs: items }));
                                  }}
                                  disabled={idx === 0}
                                  className="p-1 bg-zinc-950 border border-zinc-900 text-zinc-500 hover:text-white disabled:opacity-20 transition-all text-[8px] font-mono"
                                  title="Subir"
                                >
                                  ▲
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (idx === (siteSettingsState.faqs?.length || 0) - 1) return;
                                    const items = [...(siteSettingsState.faqs || [])];
                                    const temp = items[idx];
                                    items[idx] = items[idx + 1];
                                    items[idx + 1] = temp;
                                    setSiteSettingsState(prev => ({ ...prev, faqs: items }));
                                  }}
                                  disabled={idx === (siteSettingsState.faqs?.length || 0) - 1}
                                  className="p-1 bg-zinc-950 border border-zinc-900 text-zinc-500 hover:text-white disabled:opacity-20 transition-all text-[8px] font-mono"
                                  title="Descer"
                                >
                                  ▼
                                </button>
                                <button 
                                  type="button"
                                  onClick={() => {
                                    if(confirm("Deseja realmente remover esta pergunta?")) {
                                      const updated = siteSettingsState.faqs?.filter(f => f.id !== faq.id) || [];
                                      setSiteSettingsState(prev => ({ ...prev, faqs: updated }));
                                    }
                                  }}
                                  className="p-1 bg-zinc-950 border border-zinc-900 text-zinc-500 hover:text-red-500 transition-all"
                                  title="Remover Pergunta"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>

                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                  <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest block">Pergunta PT</label>
                                  <input 
                                    type="text"
                                    value={faq.question_pt || ''}
                                    onChange={(e) => {
                                      const updated = [...(siteSettingsState.faqs || [])];
                                      updated[idx].question_pt = e.target.value;
                                      setSiteSettingsState(prev => ({ ...prev, faqs: updated }));
                                    }}
                                    className="w-full bg-zinc-950 border border-zinc-900 p-2 text-white text-[10px] outline-none focus:border-[#ff9eb5] font-sans"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest block">Pergunta EN</label>
                                  <input 
                                    type="text"
                                    value={faq.question_en || ''}
                                    onChange={(e) => {
                                      const updated = [...(siteSettingsState.faqs || [])];
                                      updated[idx].question_en = e.target.value;
                                      setSiteSettingsState(prev => ({ ...prev, faqs: updated }));
                                    }}
                                    className="w-full bg-zinc-950 border border-zinc-900 p-2 text-white text-[10px] outline-none focus:border-[#ff9eb5] font-sans"
                                  />
                                </div>
                              </div>

                              <div className="space-y-1">
                                <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest block">Resposta PT</label>
                                <textarea 
                                  rows={2}
                                  value={faq.answer_pt || ''}
                                  onChange={(e) => {
                                    const updated = [...(siteSettingsState.faqs || [])];
                                    updated[idx].answer_pt = e.target.value;
                                    setSiteSettingsState(prev => ({ ...prev, faqs: updated }));
                                  }}
                                  className="w-full bg-zinc-950 border border-zinc-900 p-2 text-white text-[10px] outline-none focus:border-[#ff9eb5] font-sans resize-none"
                                />
                              </div>

                              <div className="space-y-1">
                                <label className="text-[8px] font-black text-zinc-500 uppercase tracking-widest block">Resposta EN</label>
                                <textarea 
                                  rows={2}
                                  value={faq.answer_en || ''}
                                  onChange={(e) => {
                                    const updated = [...(siteSettingsState.faqs || [])];
                                    updated[idx].answer_en = e.target.value;
                                    setSiteSettingsState(prev => ({ ...prev, faqs: updated }));
                                  }}
                                  className="w-full bg-zinc-950 border border-zinc-900 p-2 text-white text-[10px] outline-none focus:border-[#ff9eb5] font-sans resize-none"
                                />
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    <button 
                      type="button"
                      onClick={async () => {
                        setIsSyncing(true);
                        const saveRes = await database.saveSettings(siteSettingsState);
                        if (saveRes.success) {
                          onUpdateSiteSettings(siteSettingsState);
                          alert("Perguntas do F.A.Q. salvas com sucesso!");
                        } else {
                          alert(`Erro: ${saveRes.message || 'Falha ao salvar.'}`);
                        }
                        setIsSyncing(false);
                      }}
                      disabled={isSyncing}
                      className="w-full bg-[#ff9eb5] hover:bg-[#ff9eb5]/90 text-white py-4 font-black uppercase text-[10px] flex items-center justify-center gap-2 tracking-widest transition-all mt-4 border-b-4 border-rose-400 hover:border-[#ff9eb5] active:scale-95"
                    >
                      {isSyncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Salvar Banco de F.A.Q
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'gallery' && (
          <div className="space-y-12">
            {!isAddingArt ? (
              <button onClick={() => setIsAddingArt(true)} className="bg-[#ff9eb5] text-white px-10 py-5 font-black uppercase text-xs flex items-center gap-3 hover:scale-105 transition-all">
                <Plus className="w-4 h-4" /> Adicionar Nova Obra
              </button>
            ) : (
              <div ref={editFormRef} className="bg-zinc-900/40 border border-white/5 p-8 md:p-12 animate-in slide-in-from-top-4 duration-500">
                <div className="flex justify-between items-center mb-10 border-b border-white/5 pb-6">
                   <h3 className="text-white font-black uppercase text-sm">{editingArt ? 'Editando Obra' : 'Nova Obra'}</h3>
                   <button onClick={resetForm} className="text-zinc-500 hover:text-white transition-colors"><X className="w-6 h-6" /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-[400px_1fr] gap-16">
                  <div className="space-y-6">
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="aspect-[9/14] bg-black border-2 border-dashed border-zinc-800 flex flex-col items-center justify-center cursor-pointer hover:border-[#ff9eb5] transition-all overflow-hidden relative group"
                    >
                      {previewUrl ? (
                        <>
                          {isVideoUrl(previewUrl) ? (
                            <video src={previewUrl} autoPlay loop muted className="w-full h-full object-cover" />
                          ) : (
                            <img src={previewUrl} className="w-full h-full object-cover" />
                          )}
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all">
                            <RefreshCw className="w-10 h-10 text-white" />
                          </div>
                        </>
                      ) : (
                        <>
                          <Upload className="w-12 h-12 text-zinc-700 mb-4" />
                          <p className="text-[10px] font-black uppercase text-zinc-600 tracking-widest text-center px-4">Arraste ou clique para selecionar Mídia</p>
                        </>
                      )}
                    </div>
                    <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelect} accept="image/*,video/mp4" />
                    
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center justify-between p-4 bg-black border border-zinc-800">
                        <span className="text-[9px] font-black text-zinc-500 uppercase flex items-center gap-2">
                          <FileVideo className="w-3.5 h-3.5" /> Animado?
                        </span>
                        <button onClick={() => setNewArt({...newArt, isAnimated: !newArt.isAnimated})} className="text-[#ff9eb5]">
                          {newArt.isAnimated ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8 opacity-30" />}
                        </button>
                      </div>

                      <div className={`flex items-center justify-between p-4 bg-black border transition-all ${newArt.notForSale ? 'border-red-500/50' : 'border-zinc-800'}`}>
                        <span className={`text-[9px] font-black uppercase flex items-center gap-2 ${newArt.notForSale ? 'text-red-500' : 'text-zinc-500'}`}>
                          <Ban className="w-3.5 h-3.5" /> Not for Sale
                        </span>
                        <button onClick={() => setNewArt({...newArt, notForSale: !newArt.notForSale})} className={newArt.notForSale ? 'text-red-500' : 'text-zinc-500'}>
                          {newArt.notForSale ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8 opacity-30" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest">Título da Obra (PT)</label>
                        <input type="text" value={newArt.title?.pt} onChange={e => setNewArt({...newArt, title: {...newArt.title, pt: e.target.value}})} className="w-full bg-black border border-zinc-800 p-4 text-white text-xs outline-none focus:border-[#ff9eb5]" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest">Título da Obra (EN)</label>
                        <input type="text" value={newArt.title?.en} onChange={e => setNewArt({...newArt, title: {...newArt.title, en: e.target.value}})} className="w-full bg-black border border-zinc-800 p-4 text-white text-xs outline-none focus:border-[#ff9eb5]" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest">Categoria</label>
                        <select value={newArt.category} onChange={e => {
                          const cat = e.target.value as ArtCategory;
                          const defaultSub = ART_SUBCATEGORIES[cat]?.[0]?.id || '';
                          setNewArt({
                            ...newArt,
                            category: cat,
                            subcategory: defaultSub
                          });
                        }} className="w-full bg-black border border-zinc-800 p-4 text-white text-xs outline-none focus:border-[#ff9eb5]">
                          {Object.values(ArtCategory).map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest">Investimento ($)</label>
                        <div className="relative">
                          <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-600" />
                          <input type="number" value={newArt.price} onChange={e => setNewArt({...newArt, price: parseFloat(e.target.value)})} className="w-full bg-black border border-zinc-800 p-4 pl-10 text-white text-xs outline-none focus:border-[#ff9eb5]" />
                        </div>
                      </div>
                    </div>

                    {newArt.category && ART_SUBCATEGORIES[newArt.category] && (
                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest">
                          {language === 'en' ? `Subcategory (${newArt.category})` : `Subcategoria (${newArt.category})`}
                        </label>
                        <select 
                          value={newArt.subcategory || (ART_SUBCATEGORIES[newArt.category]?.[0]?.id || '')} 
                          onChange={e => setNewArt({...newArt, subcategory: e.target.value})} 
                          className="w-full bg-black border border-zinc-800 p-4 text-white text-xs outline-none focus:border-[#ff9eb5]"
                        >
                          {ART_SUBCATEGORIES[newArt.category].map(sub => (
                            <option key={sub.id} value={sub.id}>
                              {sub.label[language] || sub.id}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="space-y-2">
                      <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest">
                        {language === 'en' ? 'Art Type' : 'Tipo de Arte'}
                      </label>
                      <select 
                        value={newArt.artType || 'Single Artwork'} 
                        onChange={e => setNewArt({...newArt, artType: e.target.value})} 
                        className="w-full bg-black border border-zinc-800 p-4 text-white text-xs outline-none focus:border-[#ff9eb5]"
                      >
                        {ART_TYPES.map(type => (
                          <option key={type.id} value={type.id}>
                            {language === 'en' ? type.label.en : type.label.pt}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest flex items-center gap-2">
                        <LinkIcon className="w-3.5 h-3.5 text-[#ff9eb5]" /> Link do Mercado Steam (Papel de Parede)
                      </label>
                      <input 
                        type="text" 
                        value={newArt.steamBackgroundUrl} 
                        onChange={e => setNewArt({...newArt, steamBackgroundUrl: e.target.value})} 
                        placeholder="https://steamcommunity.com/market/listings/753/..."
                        className="w-full bg-black border border-zinc-800 p-4 text-white text-xs outline-none focus:border-[#ff9eb5] font-mono" 
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest">Descrição (PT)</label>
                        <textarea value={newArt.description?.pt} onChange={e => setNewArt({...newArt, description: {...newArt.description, pt: e.target.value}})} className="w-full bg-black border border-zinc-800 p-4 text-white text-xs h-24 outline-none focus:border-[#ff9eb5] resize-none" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-zinc-500 uppercase tracking-widest">Descrição (EN)</label>
                        <textarea value={newArt.description?.en} onChange={e => setNewArt({...newArt, description: {...newArt.description, en: e.target.value}})} className="w-full bg-black border border-zinc-800 p-4 text-white text-xs h-24 outline-none focus:border-[#ff9eb5] resize-none" />
                      </div>
                    </div>

                    <div className="flex gap-4 pt-6 border-t border-white/5">
                      <button 
                        onClick={handleUploadRealArt} 
                        disabled={isUploading}
                        className="flex-1 bg-white hover:bg-[#ff9eb5] text-black hover:text-white py-5 font-black uppercase text-[10px] flex items-center justify-center gap-3 transition-all active:scale-95 shadow-xl"
                      >
                        {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {editingArt ? 'Atualizar Obra' : 'Sincronizar com Servidor'}
                      </button>
                      <button onClick={resetForm} className="px-10 border border-zinc-800 text-zinc-500 font-black uppercase text-[10px] hover:text-white hover:border-white transition-all">Cancelar</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {safeArts.map(art => (
                <div key={art.id} className="bg-zinc-900/40 p-4 border border-white/5 group relative">
                  <div className="aspect-video sm:aspect-[9/14] bg-black mb-4 overflow-hidden relative">
                    {isVideoUrl(art.videoUrl || art.imageUrl) ? (
                      <video src={art.videoUrl || art.imageUrl} autoPlay loop muted className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
                    ) : (
                      <img src={art.imageUrl} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
                    )}
                    {art.notForSale && (
                      <div className="absolute top-2 right-2 bg-red-500 text-white text-[8px] font-black px-2 py-1 uppercase tracking-widest">NFS</div>
                    )}
                    
                    <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-4 opacity-0 group-hover:opacity-100 transition-opacity">
                       <button onClick={() => handleEditClick(art)} className="w-32 py-3 bg-white text-black font-black uppercase text-[9px] flex items-center justify-center gap-2 hover:bg-[#ff9eb5] hover:text-white transition-all"><Edit2 className="w-3 h-3" /> Editar</button>
                       <button onClick={() => handleDeleteArt(art.id)} className="w-32 py-3 bg-zinc-900 text-red-500 border border-red-500/30 font-black uppercase text-[9px] flex items-center justify-center gap-2 hover:bg-red-500 hover:text-white transition-all"><Trash2 className="w-3 h-3" /> Excluir</button>
                    </div>
                  </div>
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white text-[10px] font-black uppercase truncate group-hover:text-[#ff9eb5] transition-colors">{art.title.pt}</h4>
                      <p className="text-zinc-600 text-[8px] font-black uppercase tracking-widest">{art.category}{art.subcategory ? ` > ${art.subcategory}` : ''}</p>
                    </div>
                    <div className="text-right ml-4">
                      <p className={`font-mono text-xs font-black ${art.notForSale ? 'text-zinc-700 italic' : 'text-[#ff9eb5]'}`}>
                        {art.notForSale ? 'NFS' : `$ ${art.price.toFixed(2)}`}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'categories' && (
          <div className="space-y-12">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-zinc-900/60 border border-white/5 p-8 backdrop-blur-md gap-6">
               <div className="flex-1">
                  <h3 className="text-white font-black uppercase text-sm tracking-widest">Sincronia Global de Capas</h3>
                  <p className="text-zinc-500 text-[9px] font-black uppercase tracking-[0.3em] mt-1">Publique no servidor ou baixe o JSON para upload manual</p>
               </div>
               <div className="flex flex-wrap gap-4">
                 <button onClick={handleDownloadCoversJSON} className="bg-zinc-800 text-white px-8 py-5 text-[10px] font-black uppercase flex items-center gap-4 border border-white/5 hover:bg-[#ff9eb5]"><Download className="w-4 h-4" /> Baixar category_covers.json</button>
                 <button onClick={handleGlobalSyncCovers} disabled={isSyncing} className="bg-white hover:bg-[#ff9eb5] text-black hover:text-white px-10 py-5 text-[10px] font-black uppercase flex items-center gap-4 transition-all shadow-xl active:scale-95 disabled:opacity-20">{isSyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudUpload className="w-4 h-4" />} Forçar Sincronização Direta</button>
               </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Object.values(ArtCategory).map(cat => (
                <div key={cat} className="bg-zinc-900/40 border border-white/5 p-6 flex flex-col gap-6 group hover:border-[#ff9eb5]/20 transition-all">
                  <div className="aspect-video bg-black overflow-hidden border border-white/5 relative">
                    {isVideoUrl(categoryCovers[cat]) ? (
                      <video src={categoryCovers[cat]} autoPlay loop muted className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity" />
                    ) : (
                      <img src={categoryCovers[cat] || MASTER_FALLBACK_IMAGE} className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity" />
                    )}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><h4 className="text-white font-black uppercase text-xs tracking-widest">{cat}</h4></div>
                  </div>
                  
                  {editingCategory === cat ? (
                    <div className="space-y-4 animate-in slide-in-from-top-2 duration-300">
                       <input type="text" placeholder="https://..." value={catCoverPreview || ''} onChange={(e) => setCatCoverPreview(e.target.value)} className="w-full bg-black border border-zinc-800 p-3 text-white text-[10px] outline-none focus:border-[#ff9eb5]" />
                       <input ref={catFileInputRef} type="file" className="hidden" onChange={(e) => {
                           const file = e.target.files?.[0];
                           if (file) {
                             setCatPendingFile(file);
                             setCatCoverPreview(URL.createObjectURL(file));
                           }
                       }} accept="image/*,video/mp4" />
                       <div className="flex gap-2">
                          <button onClick={() => catFileInputRef.current?.click()} className="flex-1 bg-zinc-800 text-white py-3 text-[9px] font-black uppercase hover:bg-zinc-700 transition-all">Upload</button>
                          <button onClick={handleSaveCategoryCover} disabled={isSyncing} className="flex-1 bg-[#ff9eb5] text-white py-3 text-[9px] font-black uppercase hover:bg-white hover:text-[#ff9eb5] transition-all flex items-center justify-center gap-2">{isSyncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Ok</button>
                          <button onClick={() => { setEditingCategory(null); setCatPendingFile(null); setCatCoverPreview(null); }} className="px-4 bg-zinc-950 text-zinc-500 py-3 text-[9px] font-black uppercase hover:text-white">X</button>
                       </div>
                    </div>
                  ) : (
                    <button onClick={() => { setEditingCategory(cat); setCatCoverPreview(categoryCovers[cat]); }} className="w-full py-4 border border-zinc-800 text-zinc-500 font-black uppercase text-[10px] hover:text-white hover:border-[#ff9eb5] transition-all">Editar Capa</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {activeTab === 'orders' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h3 className="text-white font-black uppercase text-sm tracking-widest">Fluxo de Pedidos</h3>
                <p className="text-zinc-600 text-[8px] font-black uppercase tracking-[0.4em]">Gestão de vendas e entregas</p>
              </div>
              <button onClick={refreshOrders} className="bg-zinc-900 border border-zinc-800 text-zinc-500 hover:text-[#ff9eb5] px-6 py-3 flex items-center gap-3 text-[10px] font-black uppercase transition-all">
                <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} /> Sincronizar
              </button>
            </div>
            <div className="grid gap-4">
              {safeOrders.map(order => (
                <div key={order.id} className="bg-zinc-900/40 border border-white/5 flex flex-col group hover:border-[#ff9eb5]/30 transition-all overflow-hidden">
                  <div onClick={() => setExpandedOrderId(expandedOrderId === order.id ? null : order.id)} className="p-6 flex flex-wrap justify-between items-center cursor-pointer hover:bg-white/5 transition-colors">
                    <div className="flex gap-10">
                      <div><p className="text-[8px] text-zinc-600 font-black uppercase tracking-widest mb-1">Identificador</p><p className="text-white font-mono text-xs">{order.id}</p></div>
                      <div><p className="text-[8px] text-zinc-600 font-black uppercase tracking-widest mb-1">Membro</p><p className="text-white font-black uppercase text-xs">{order.userName}</p></div>
                      <div><p className="text-[8px] text-zinc-600 font-black uppercase tracking-widest mb-1">Investimento</p><p className="text-[#ff9eb5] font-mono text-xs">$ {order.total.toFixed(2)}</p></div>
                    </div>
                    <div className="flex items-center gap-6">
                      <select value={order.status} onClick={(e) => e.stopPropagation()} onChange={(e) => { e.stopPropagation(); database.updateOrderStatus(order.id, e.target.value as OrderStatus).then(refreshOrders); }} className={`bg-black border border-zinc-800 text-[10px] font-black uppercase px-6 py-2 outline-none focus:border-[#ff9eb5] transition-all ${order.status === OrderStatus.COMPLETED ? 'text-green-500 border-green-500/20' : 'text-[#ff9eb5] border-[#ff9eb5]/20'}`}>
                        {Object.values(OrderStatus).map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleDeleteOrder(order.id); }}
                        className="p-2 bg-zinc-950 text-zinc-600 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {expandedOrderId === order.id ? <X className="w-5 h-5 text-zinc-500" /> : <ChevronDown className="w-5 h-5 text-zinc-500" />}
                    </div>
                  </div>

                  {expandedOrderId === order.id && (
                    <div className="p-8 bg-black/40 border-t border-white/5 animate-in slide-in-from-top-4 duration-300">
                       <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-10">
                          <div className="space-y-4">
                             <h4 className="text-[10px] font-black text-zinc-500 uppercase tracking-widest flex items-center gap-2 border-b border-white/5 pb-2"><UserCheck className="w-3.5 h-3.5" /> Informações do Cliente</h4>
                             <div className="grid grid-cols-2 gap-4">
                                <div><p className="text-[8px] text-zinc-600 font-black uppercase tracking-widest">E-mail</p><p className="text-white text-[11px] truncate">{order.userEmail}</p></div>
                                <div><p className="text-[8px] text-zinc-600 font-black uppercase tracking-widest">Data</p><p className="text-white text-[11px]">{order.date}</p></div>
                             </div>
                          </div>
                       </div>
                       <div className="space-y-6">
                          <h4 className="text-[10px] font-black text-zinc-500 uppercase tracking-widest flex items-center gap-2 border-b border-white/5 pb-2"><Box className="w-3.5 h-3.5" /> Itens e Especificações</h4>
                          {order.items.map((item, idx) => (
                            <div key={idx} className="bg-zinc-950/50 p-6 border border-white/5 space-y-6">
                               <div className="flex items-center gap-6">
                                  <div className="w-16 h-16 bg-black border border-white/5"><img src={item.imageUrl} className="w-full h-full object-cover" /></div>
                                  <div><h5 className="text-white font-black uppercase text-xs">{item.title.pt}</h5><p className="text-zinc-600 text-[9px] font-black uppercase tracking-widest">{item.category}</p></div>
                               </div>
                               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                  {item.customSignature && (
                                    <div className="bg-black p-4 border border-white/5">
                                      <span className="text-[8px] text-zinc-600 font-black uppercase block mb-1">Assinatura</span>
                                      <p className="artistic-font text-lg leading-none" style={{ color: item.sigColor || '#ff9eb5' }}>{item.customSignature}</p>
                                    </div>
                                  )}
                                  {item.sigColor && (
                                    <div className="bg-black p-4 border border-white/5">
                                      <span className="text-[8px] text-zinc-600 font-black uppercase block mb-1">Cor Selecionada</span>
                                      <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 rounded-full border border-white/20" style={{ backgroundColor: item.sigColor }} />
                                        <span className="text-white font-mono text-[10px]">{item.sigColor.toUpperCase()}</span>
                                      </div>
                                    </div>
                                  )}
                                  {item.discord && <div className="bg-black p-4 border border-white/5"><span className="text-[8px] text-zinc-600 font-black uppercase block mb-1">Discord</span><p className="text-white font-mono text-[11px]">{item.discord}</p></div>}
                                  {item.steamUrl && <div className="bg-black p-4 border border-white/5"><span className="text-[8px] text-zinc-600 font-black uppercase block mb-1">Steam Link</span><a href={item.steamUrl} target="_blank" rel="noopener noreferrer" className="text-zinc-400 font-mono text-[10px] hover:text-[#ff9eb5] transition-colors truncate block">{item.steamUrl}</a></div>}
                                  {item.dafontLink && (
                                    <div className="bg-black p-4 border border-white/5">
                                      <span className="text-[8px] text-[#ff9eb5] font-black uppercase block mb-1">Link da Fonte (Dafont)</span>
                                      {item.dafontLink.startsWith('http') ? (
                                        <a href={item.dafontLink} target="_blank" rel="noopener noreferrer" className="text-white font-mono text-[10px] hover:text-[#ff9eb5] transition-colors underline truncate block">{item.dafontLink}</a>
                                      ) : (
                                        <p className="text-white font-mono text-[11px]">{item.dafontLink}</p>
                                      )}
                                    </div>
                                  )}
                                  {item.customFont && item.customFont !== 'Custom' && (
                                    <div className="bg-black p-4 border border-white/5">
                                      <span className="text-[8px] text-[#ff9eb5] font-black uppercase block mb-1">Fonte Selecionada</span>
                                      <p className="text-white font-mono text-[11px]">{item.customFont}</p>
                                    </div>
                                  )}
                                  {((item.sigPosX !== undefined && item.sigPosX !== 0) || (item.sigPosY !== undefined && item.sigPosY !== 60)) && (
                                    <div className="bg-black p-4 border border-white/5"><span className="text-[8px] text-zinc-600 font-black uppercase block mb-1">Posição / Escala</span><p className="text-white font-mono text-[10px]">X: {item.sigPosX} | Y: {item.sigPosY} | S: {item.sigScale}x</p></div>
                                  )}
                                  {item.notes && (
                                    <div className="bg-black p-4 border border-white/5 col-span-1 sm:col-span-2 lg:col-span-3">
                                      <span className="text-[8px] text-[#ff9eb5] font-black uppercase block mb-1">Observações do Cliente</span>
                                      <p className="text-zinc-300 font-sans text-xs whitespace-pre-wrap">{item.notes}</p>
                                    </div>
                                  )}
                               </div>
                            </div>
                          ))}
                       </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'chats' && (
          <div className="h-[700px] flex gap-8 animate-in fade-in duration-500">
            <div className="w-96 bg-zinc-950 border border-white/5 flex flex-col">
               <div className="p-8 border-b border-white/5 bg-zinc-900/40"><h3 className="text-white font-black uppercase text-[10px] tracking-widest flex items-center gap-3"><MessageSquare className="w-4 h-4 text-[#ff9eb5]" /> Atendimento Direto</h3></div>
               <div className="flex-1 overflow-y-auto custom-scrollbar">
                  {chatThreads.map((thread) => (
                    <button key={thread.userEmail} onClick={() => setSelectedThreadEmail(thread.userEmail)} className={`w-full p-8 text-left border-b border-white/5 transition-all hover:bg-white/5 relative ${selectedThreadEmail === thread.userEmail ? 'bg-[#ff9eb5]/10' : ''}`}>
                      {thread.unreadCount > 0 && <span className="absolute top-8 right-8 w-2 h-2 bg-[#ff9eb5] rounded-full animate-pulse" />}
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-black text-[10px] uppercase truncate tracking-tight">{thread.userName}</div>
                          <div className="text-zinc-600 text-[8px] font-black uppercase truncate mt-1">{thread.userEmail}</div>
                        </div>
                        <button 
                          onClick={(e) => { e.stopPropagation(); handleDeleteChatThread(thread.userEmail); }}
                          className="p-2 text-zinc-700 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </button>
                  ))}
               </div>
            </div>
            <div className="flex-1 bg-zinc-950 border border-white/5 flex flex-col relative overflow-hidden">
               {selectedThreadEmail ? (
                 <>
                   <div className="p-8 border-b border-white/5 bg-zinc-900/40 flex justify-between items-center">
                      <div className="flex flex-col">
                        <h4 className="text-white font-black uppercase text-xs tracking-tighter">{chatThreads.find(t => t.userEmail === selectedThreadEmail)?.userName}</h4>
                        <span className="text-zinc-600 text-[8px] font-black uppercase tracking-widest">{selectedThreadEmail}</span>
                      </div>
                      <button onClick={refreshChats} className="p-3 bg-zinc-900 text-[#ff9eb5] hover:bg-[#ff9eb5] hover:text-white transition-all"><RefreshCw className="w-4 h-4" /></button>
                   </div>
                   <div className="flex-1 overflow-y-auto p-10 space-y-8 bg-black/40 custom-scrollbar">
                      {chatThreads.find(t => t.userEmail === selectedThreadEmail)?.messages.map((msg) => (
                        <div key={msg.id} className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                           <div className={`max-w-[85%] p-6 text-xs border relative ${msg.sender === 'admin' ? 'bg-[#ff9eb5]/10 border-[#ff9eb5]/20 text-white' : 'bg-zinc-900 border-white/10 text-zinc-300'}`}>
                              <div className="absolute -top-3 left-0 bg-black px-2 text-[7px] font-black uppercase text-zinc-700 tracking-widest">{msg.sender}</div>
                              {msg.imageUrl && <img src={msg.imageUrl} className="w-full mb-4 rounded border border-white/5 shadow-2xl" />}
                              <p className="leading-relaxed">{msg.text}</p>
                              <div className="text-[7px] mt-4 opacity-50 uppercase font-black tracking-widest flex items-center gap-2"><Clock className="w-2.5 h-2.5" /> {msg.timestamp}</div>
                           </div>
                        </div>
                      ))}
                      <div ref={chatEndRef} />
                   </div>
                   <div className="p-8 bg-zinc-900/40 border-t border-white/5">
                      <div className="flex gap-4">
                        <input type="text" placeholder="Transmitir..." value={adminReply} onChange={(e) => setAdminReply(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSendAdminReply()} className="flex-1 bg-black border border-zinc-800 p-5 text-white text-xs outline-none focus:border-[#ff9eb5] transition-all" />
                        <button onClick={handleSendAdminReply} className="bg-white hover:bg-[#ff9eb5] text-black hover:text-white px-10 py-5 text-[10px] font-black uppercase transition-all shadow-xl active:scale-95">Transmitir</button>
                      </div>
                   </div>
                 </>
               ) : (
                 <div className="flex-1 flex flex-col items-center justify-center opacity-10 text-center px-10">
                   <MessageSquare className="w-32 h-32 mb-10 text-white" />
                   <h4 className="text-xl font-black text-white uppercase tracking-tighter">Câmara de Atendimento</h4>
                 </div>
               )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
